/**
 * Canvas2D effect implementations.
 *
 * Registered as the active factory set when Canvas2DRenderer is constructed:
 *   effectSystem.setFactories({ Explosion: CanvasExplosion, ... })
 *
 * Each class renders itself by drawing directly onto a Canvas2D context in
 * its update(ctx, dt) method.  EffectSystem.updateByLayer drives the loop.
 */

import {
    FIREWORKS_PARTICLE_COUNT, FIREWORKS_RGB, FIREWORKS_DURATION,
    EXPLOSION_END_RGB, ICON_EFFECT_RGB, POWER_IMPACT_DURATION,
} from '../constants.js';

/** @abstract */
class CanvasEffect {
    /** @param {number} duration */
    constructor(duration) {
        /** @type {number} */
        this.time = 0;
        /** @type {number} */
        this.duration = duration;
        /** @type {'top'|'ground'|'mid'} */
        this.layer = 'top';
    }

    /**
     * @param {CanvasRenderingContext2D} ctx
     * @param {number} dt
     */
    update(ctx, dt) {
        this.time += dt;
    }

    /** @returns {boolean} */
    isDone() { return this.time >= this.duration; }
}

/** Canvas2D expanding circle explosion. */
export class CanvasExplosion extends CanvasEffect {
    /**
     * @param {number} duration
     * @param {number} x
     * @param {number} y
     * @param {number} startSize
     * @param {number} finalSize
     * @param {[number,number,number]} [startRgb]
     * @param {[number,number,number]} [endRgb]
     */
    constructor(duration, x, y, startSize, finalSize, startRgb, endRgb) {
        super(duration);
        this.x         = x;
        this.y         = y;
        this.startSize = startSize;
        this.finalSize = finalSize;
        // Start color is Canvas2D-only: the WebGL backend bakes it into the
        // explosion shader, so it has no shared-constant counterpart.
        this.startRgb  = startRgb || [255, 255, 100];
        this.endRgb    = endRgb   || EXPLOSION_END_RGB;
    }

    /** @param {CanvasRenderingContext2D} ctx @param {number} dt */
    update(ctx, dt) {
        const factor = this.time / this.duration;
        const r    = Math.floor(this.startRgb[0] + (this.endRgb[0] - this.startRgb[0]) * factor);
        const g    = Math.floor(this.startRgb[1] + (this.endRgb[1] - this.startRgb[1]) * factor);
        const b    = Math.floor(this.startRgb[2] + (this.endRgb[2] - this.startRgb[2]) * factor);
        const size = this.startSize + (this.finalSize - this.startSize) * factor;
        ctx.fillStyle = `rgba(${r},${g},${b},1.0)`;
        ctx.beginPath();
        ctx.arc(this.x, this.y, size, 0, Math.PI * 2);
        ctx.closePath();
        ctx.fill();
        super.update(ctx, dt);
    }
}

/** Canvas2D burst-particle fireworks effect. */
export class CanvasFireworks extends CanvasEffect {
    /**
     * @param {number} x
     * @param {number} y
     * @param {[number,number,number]} colorRgb
     * @param {number} [duration]
     */
    constructor(x, y, colorRgb, duration = FIREWORKS_DURATION) {
        super(duration);
        this.x        = x;
        this.y        = y;
        this.colorRgb = colorRgb || FIREWORKS_RGB;
        this.particles = [];
        for (let i = 0; i < FIREWORKS_PARTICLE_COUNT; i++) {
            const angle = (Math.PI * 2 * i) / FIREWORKS_PARTICLE_COUNT + (Math.random() - 0.5) * 0.4;
            const speed = 40 + Math.random() * 60;
            this.particles.push({ x, y, vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed });
        }
    }

    /** @param {CanvasRenderingContext2D} ctx @param {number} dt */
    update(ctx, dt) {
        const alpha = 1 - (this.time / this.duration);
        const [r, g, b] = this.colorRgb;
        const fillStyle = `rgba(${r},${g},${b},${alpha.toFixed(2)})`;
        for (const p of this.particles) {
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            ctx.beginPath();
            ctx.arc(p.x, p.y, 1.8, 0, Math.PI * 2);
            ctx.fillStyle = fillStyle;
            ctx.fill();
        }
        super.update(ctx, dt);
    }
}

/** Canvas2D spinning tinted-SVG icon effect (conversions, freeze, teleport, etc.). */
export class CanvasIconEffect extends CanvasEffect {
    /**
     * @param {number} x
     * @param {number} y
     * @param {number} radius
     * @param {[number,number,number]} colorRgb
     * @param {number} [duration]
     * @param {'top'|'ground'} [layer]
     * @param {number} [startScale]
     * @param {string} [iconSrc]
     * @param {number} [rotations]
     */
    constructor(x, y, radius, colorRgb, duration = 1.0, layer = 'ground', startScale = 0.5, iconSrc, rotations = 2) {
        super(duration);
        this.layer      = layer;
        this.x          = x;
        this.y          = y;
        this.radius     = radius;
        this.colorRgb   = colorRgb || ICON_EFFECT_RGB;
        this.startScale = startScale;
        this.rotations  = rotations;
        /** @type {HTMLImageElement|null} */
        this._img    = null;
        /** @type {HTMLCanvasElement|null} */
        this._tinted = null;
        if (typeof Image !== 'undefined') {
            this._img = new Image();
            this._img.src = iconSrc ?? '';
        }
    }

    /** @returns {HTMLCanvasElement} */
    _buildTinted() {
        const sz = 256;
        const c  = document.createElement('canvas');
        c.width = sz; c.height = sz;
        const cx = c.getContext('2d');
        cx.drawImage(this._img, 0, 0, sz, sz);
        cx.globalCompositeOperation = 'source-atop';
        const [r, g, b] = this.colorRgb;
        cx.fillStyle = `rgba(${r},${g},${b},0.75)`;
        cx.fillRect(0, 0, sz, sz);
        return c;
    }

    /** @param {CanvasRenderingContext2D} ctx @param {number} dt */
    update(ctx, dt) {
        if (!this._img || !this._img.complete || this._img._failed) { super.update(ctx, dt); return; }
        if (!this._tinted) this._tinted = this._buildTinted();
        const t        = this.time / this.duration;
        const alpha    = t < 0.7 ? 1.0 : (1.0 - t) / 0.3;
        const size     = this.radius * 2 * (this.startScale + (1 - this.startScale) * t);
        const rotation = t * Math.PI * 2 * this.rotations;
        ctx.save();
        ctx.globalAlpha = Math.max(0, alpha);
        ctx.translate(this.x, this.y);
        ctx.rotate(rotation);
        ctx.drawImage(this._tinted, -size / 2, -size / 2, size, size);
        ctx.restore();
        super.update(ctx, dt);
    }
}

/** Canvas2D fading filled circle that flashes the blast radius when a power fires. */
export class CanvasPowerImpact extends CanvasEffect {
    /**
     * @param {number} x
     * @param {number} y
     * @param {number} radius
     * @param {string} color  CSS color string
     */
    constructor(x, y, radius, color) {
        super(POWER_IMPACT_DURATION);
        this.x      = x;
        this.y      = y;
        this.radius = radius;
        this.color  = color;
        this.layer  = 'mid';
    }

    /** @param {CanvasRenderingContext2D} ctx @param {number} dt */
    update(ctx, dt) {
        const alpha = 1 - (this.time / this.duration);
        ctx.save();
        ctx.globalAlpha = Math.max(0, alpha);
        ctx.fillStyle = this.color;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        super.update(ctx, dt);
    }
}

/**
 * Factory map for Canvas2D mode.  Pass to effectSystem.setFactories().
 * @type {Record<string, (...args: unknown[]) => CanvasEffect>}
 */
export const CANVAS_EFFECT_FACTORIES = {
    Explosion:          (...a) => new CanvasExplosion(...a),
    Fireworks:          (...a) => new CanvasFireworks(...a),
    IconEffect:         (...a) => new CanvasIconEffect(...a),
    PowerImpactCircle:  (...a) => new CanvasPowerImpact(...a),
};
